/**
 * StrategySyncPlanView
 *
 * Shared plan-review surface for all four strategy<->onboarding sync verbs
 * (push / pull / compile / lift). Renders the dry-run result as a per-screen
 * diff table grouped by block plus dedicated sections for variables, AI asset
 * slots and loose (unassigned) screens — each carrying its own resolution
 * control with safe defaults (funnel polish wins, experiments merge as
 * alternates, target variable definitions are preserved).
 *
 * The component is controlled: the parent owns the `resolutions` object and
 * receives every change through `onResolutionsChange`.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha } from '@mui/material/styles';
import type {
  CliStrategyAssetSlotDiffEntry,
  CliStrategyForeignTagEntry,
  CliStrategyLooseScreenEntry,
  CliStrategyPushPlanEntry,
  CliStrategyPushScreenAction,
  CliStrategySyncResolutions,
  CliStrategyVariableDiffEntry,
} from '@modules/shared-types';

export type StrategySyncDirection = 'push' | 'pull' | 'lift' | 'compile';

/** Pull plan rows only carry screenId+action; push/compile rows are richer. */
export type StrategySyncPlanEntry =
  { screenId: string; action: CliStrategyPushScreenAction }
  & Partial<Omit<CliStrategyPushPlanEntry, 'screenId' | 'action'>>;

export interface StrategySyncPlanData {
  plan?: StrategySyncPlanEntry[];
  warnings?: string[];
  variableDiff?: CliStrategyVariableDiffEntry[];
  assetDiff?: CliStrategyAssetSlotDiffEntry[];
  looseScreens?: CliStrategyLooseScreenEntry[];
  foreignTagIgnored?: CliStrategyForeignTagEntry[];
}

export interface StrategySyncBlockOption {
  blockEntityId: string;
  name?: string;
}

export interface StrategySyncPlanViewProps {
  data: StrategySyncPlanData | null;
  direction: StrategySyncDirection;
  loading?: boolean;
  error?: string | null;
  /** e.g. "funnelId / versionId" */
  targetLabel?: string | null;
  /** Loose-screen placement picker (lift/pull confirm only). */
  showLooseSection?: boolean;
  /** Blocks offered in the loose-screen placement picker. */
  availableBlocks?: StrategySyncBlockOption[];
  resolutions: CliStrategySyncResolutions;
  onResolutionsChange: (next: CliStrategySyncResolutions) => void;
}

const ACTION_LABELS: Record<CliStrategyPushScreenAction, { label: string; color: 'success' | 'warning' | 'default' | 'error' }> = {
  would_create: { label: 'create', color: 'success' },
  would_replace: { label: 'replace', color: 'warning' },
  would_preserve: { label: 'preserve', color: 'default' },
  would_delete: { label: 'delete', color: 'error' },
};

const CHANGE_KIND_COLORS: Record<string, 'primary' | 'secondary' | 'info'> = {
  copy: 'primary',
  edge: 'secondary',
  asset: 'info',
};

/** Side naming depends on the verb: push flows strategy->funnel, pull/lift flow funnel->strategy. */
function sideLabels(direction: StrategySyncDirection): { source: string; target: string } {
  return direction === 'push' || direction === 'compile'
    ? { source: 'strategy', target: 'funnel' }
    : { source: 'funnel', target: 'strategy' };
}

/** Action a row lands on under the CURRENT resolutions (conflict toggle preview). */
export function effectiveRowAction(
  row: StrategySyncPlanEntry,
  resolutions: CliStrategySyncResolutions,
): CliStrategyPushScreenAction {
  if (row.conflict && (resolutions.screens?.[row.screenId] ?? 'funnel') === 'strategy') {
    return 'would_replace';
  }
  if (row.conflict) {
    return 'would_preserve';
  }
  return row.action;
}

/**
 * True when confirming would write nothing: every screen row stays preserved,
 * no asset patches ride along, no variable/loose resolutions cause writes.
 */
export function isSyncPlanNoOp(
  data: StrategySyncPlanData | null,
  resolutions: CliStrategySyncResolutions,
): boolean {
  if (!data) return false;
  const anyScreenChange = (data.plan ?? []).some(
    (row) => effectiveRowAction(row, resolutions) !== 'would_preserve',
  );
  const anyAssetPatch = (data.assetDiff ?? []).some(
    (entry) => entry.status === 'source-only' || entry.status === 'both-diverged',
  );
  const anyVariableChange = (data.variableDiff ?? []).some((entry) => {
    if (entry.status === 'unchanged') return false;
    const resolution = resolutions.variables?.[entry.variableId] ?? entry.defaultResolution;
    if (resolution === 'preserve') return false;
    if (resolution === 'drop' && entry.status === 'added') return false;
    return true;
  });
  const anyLoosePlacement = Object.values(resolutions.looseScreens ?? {}).some(
    (entry) => entry.action !== 'keep-loose',
  );
  return !anyScreenChange && !anyAssetPatch && !anyVariableChange && !anyLoosePlacement;
}

type LooseOption =
  | { kind: 'keep'; label: string }
  | { kind: 'block'; label: string; blockEntityId: string; suggested: boolean }
  | { kind: 'new'; label: string };

const SECTION_HEADER_SX = {
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

const HEAD_CELL_SX = { fontWeight: 700, bgcolor: 'action.hover' } as const;

export const StrategySyncPlanView: React.FC<StrategySyncPlanViewProps> = ({
  data,
  direction,
  loading = false,
  error = null,
  targetLabel = null,
  showLooseSection = false,
  availableBlocks = [],
  resolutions,
  onResolutionsChange,
}) => {
  const sides = sideLabels(direction);
  const [newBlockNames, setNewBlockNames] = useState<Record<string, string>>({});

  const plan = useMemo(() => data?.plan ?? [], [data]);

  const counts = useMemo(() => {
    const totals: Record<CliStrategyPushScreenAction, number> = {
      would_create: 0,
      would_replace: 0,
      would_preserve: 0,
      would_delete: 0,
    };
    for (const row of plan) {
      totals[effectiveRowAction(row, resolutions)] += 1;
    }
    return totals;
  }, [plan, resolutions]);

  const groupedRows = useMemo(() => {
    const groups: Array<{ blockEntityId: string | null; rows: StrategySyncPlanEntry[] }> = [];
    for (const row of plan) {
      const blockEntityId = row.blockEntityId ?? null;
      const last = groups[groups.length - 1];
      if (last && last.blockEntityId === blockEntityId) {
        last.rows.push(row);
      } else {
        groups.push({ blockEntityId, rows: [row] });
      }
    }
    return groups;
  }, [plan]);

  const visibleVariables = useMemo(
    () => (data?.variableDiff ?? []).filter((entry) => entry.status !== 'unchanged'),
    [data],
  );

  const visibleAssets = useMemo(
    () => (data?.assetDiff ?? []).filter((entry) => entry.status !== 'both-equal'),
    [data],
  );

  const setScreenResolution = useCallback((screenId: string, winner: 'funnel' | 'strategy') => {
    onResolutionsChange({
      ...resolutions,
      screens: { ...(resolutions.screens ?? {}), [screenId]: winner },
    });
  }, [onResolutionsChange, resolutions]);

  const setVariableResolution = useCallback((variableId: string, resolution: string) => {
    onResolutionsChange({
      ...resolutions,
      variables: {
        ...(resolutions.variables ?? {}),
        [variableId]: resolution as NonNullable<CliStrategySyncResolutions['variables']>[string],
      },
    });
  }, [onResolutionsChange, resolutions]);

  const setAssetResolution = useCallback((entry: CliStrategyAssetSlotDiffEntry, experimentId: string) => {
    const nextSlots = { ...(resolutions.assetSlots ?? {}) };
    const perScreen = { ...(nextSlots[entry.screenId] ?? {}) };
    if (!experimentId || experimentId === entry.targetSelectedExperimentId) {
      delete perScreen[entry.slotKey];
    } else {
      perScreen[entry.slotKey] = { merge: true, selectedExperimentId: experimentId };
    }
    if (Object.keys(perScreen).length > 0) {
      nextSlots[entry.screenId] = perScreen;
    } else {
      delete nextSlots[entry.screenId];
    }
    onResolutionsChange({ ...resolutions, assetSlots: nextSlots });
  }, [onResolutionsChange, resolutions]);

  const setLooseResolution = useCallback((screenId: string, option: LooseOption | null, newBlockName?: string) => {
    const nextLoose = { ...(resolutions.looseScreens ?? {}) };
    if (!option || option.kind === 'keep') {
      delete nextLoose[screenId];
    } else if (option.kind === 'block') {
      nextLoose[screenId] = { action: 'existing-block', blockEntityId: option.blockEntityId };
    } else {
      nextLoose[screenId] = { action: 'new-block', newBlockName: (newBlockName ?? newBlockNames[screenId] ?? '').trim() || undefined };
    }
    onResolutionsChange({ ...resolutions, looseScreens: nextLoose });
  }, [newBlockNames, onResolutionsChange, resolutions]);

  const looseOptionsFor = useCallback((row: CliStrategyLooseScreenEntry): LooseOption[] => {
    const suggested = new Set(row.suggestedBlockEntityIds ?? []);
    const blockOptions = availableBlocks
      .filter((block) => block.blockEntityId && block.blockEntityId !== '_untagged')
      .map((block) => ({
        kind: 'block' as const,
        blockEntityId: block.blockEntityId,
        label: block.name ? `${block.name} — ${block.blockEntityId}` : block.blockEntityId,
        suggested: suggested.has(block.blockEntityId),
      }));
    blockOptions.sort((a, b) => Number(b.suggested) - Number(a.suggested));
    return [
      { kind: 'keep', label: 'Keep loose' },
      ...blockOptions,
      { kind: 'new', label: '+ Create new block…' },
    ];
  }, [availableBlocks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }} data-testid="strategy-sync-plan-loading">
        <CircularProgress size={32} />
      </Box>
    );
  }

  const noOp = data ? isSyncPlanNoOp(data, resolutions) : false;

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" data-testid="strategy-sync-error">
          {error}
        </Alert>
      )}

      {targetLabel && (
        <Typography variant="body2" color="text.secondary">
          Linked onboarding version: {targetLabel}
        </Typography>
      )}

      {data && plan.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="strategy-sync-plan-counts">
          {(Object.keys(ACTION_LABELS) as CliStrategyPushScreenAction[]).map((action) => (
            <Chip
              key={action}
              size="small"
              color={ACTION_LABELS[action].color}
              variant={counts[action] > 0 ? 'filled' : 'outlined'}
              label={`${counts[action]} ${ACTION_LABELS[action].label}`}
              data-testid={`strategy-sync-count-${ACTION_LABELS[action].label}`}
            />
          ))}
        </Stack>
      )}

      {noOp && (
        <Alert severity="info" data-testid="strategy-sync-noop">
          Everything is already in sync — nothing to change. Canvas polish on all
          {' '}{counts.would_preserve} screens is kept.
        </Alert>
      )}

      {data && plan.length > 0 && !noOp && (
        <Stack spacing={1}>
          <Typography sx={SECTION_HEADER_SX}>Screen changes</Typography>
          <TableContainer sx={{ maxHeight: 340, overflowX: 'auto' }}>
            <Table size="small" stickyHeader data-testid="strategy-sync-diff-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEAD_CELL_SX, width: '40%' }}>Screen</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Action</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Changes</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, width: 230 }}>Conflict resolution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRows.map((group, groupIndex) => (
                  <React.Fragment key={group.blockEntityId ?? `group-${groupIndex}`}>
                    {group.blockEntityId && (
                      <TableRow data-testid={`strategy-sync-block-group-${group.blockEntityId}`}>
                        <TableCell
                          colSpan={4}
                          sx={{ bgcolor: 'action.selected', fontWeight: 600, fontSize: '0.78rem' }}
                        >
                          Block {group.blockEntityId}
                        </TableCell>
                      </TableRow>
                    )}
                    {group.rows.map((row) => {
                      const effectiveAction = effectiveRowAction(row, resolutions);
                      const actionMeta = ACTION_LABELS[effectiveAction];
                      const conflictWinner = resolutions.screens?.[row.screenId] ?? 'funnel';
                      return (
                        <TableRow
                          key={row.screenId}
                          data-testid={`strategy-sync-row-${row.screenId}`}
                          sx={row.conflict ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) } : undefined}
                        >
                          <TableCell>
                            <Typography variant="body2" component="span">
                              {row.screenName || row.screenId}
                            </Typography>
                            {row.screenName && (
                              <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                                {row.screenId}
                              </Typography>
                            )}
                            {row.conflict && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <WarningAmberIcon sx={{ fontSize: 14 }} /> edited on both sides
                              </Typography>
                            )}
                            {row.reason === 'no-baseline' && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                data-testid={`strategy-sync-row-warning-${row.screenId}`}
                              >
                                <WarningAmberIcon sx={{ fontSize: 14 }} /> no baseline — will overwrite
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={actionMeta.color}
                              variant={effectiveAction === 'would_preserve' ? 'outlined' : 'filled'}
                              label={actionMeta.label}
                            />
                          </TableCell>
                          <TableCell>
                            {(row.changeKinds ?? []).length > 0 ? (
                              <Stack direction="row" spacing={0.5}>
                                {(row.changeKinds ?? []).map((kind) => (
                                  <Chip
                                    key={kind}
                                    size="small"
                                    variant="outlined"
                                    color={CHANGE_KIND_COLORS[kind] ?? 'default'}
                                    label={kind}
                                    data-testid={`strategy-sync-row-kind-${row.screenId}-${kind}`}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.conflict && (
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={conflictWinner}
                                onChange={(_, value) => {
                                  if (value) setScreenResolution(row.screenId, value);
                                }}
                                data-testid={`strategy-sync-conflict-toggle-${row.screenId}`}
                              >
                                <ToggleButton value="funnel" sx={{ textTransform: 'none', py: 0.25 }}>
                                  Keep funnel
                                </ToggleButton>
                                <ToggleButton value="strategy" sx={{ textTransform: 'none', py: 0.25 }}>
                                  Take strategy
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {showLooseSection && (data?.looseScreens ?? []).length > 0 && (
        <Stack spacing={1} data-testid="strategy-sync-loose-section">
          <Typography sx={SECTION_HEADER_SX}>
            Unassigned screens · choose where each goes
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Screen</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Type</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, width: 300 }}>Place into</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.looseScreens ?? []).map((row) => {
                const options = looseOptionsFor(row);
                const current = resolutions.looseScreens?.[row.screenId];
                const selected = !current || current.action === 'keep-loose'
                  ? options[0]
                  : current.action === 'new-block'
                    ? options[options.length - 1]
                    : options.find((option) => option.kind === 'block' && option.blockEntityId === current.blockEntityId) ?? options[0];
                return (
                  <TableRow key={row.screenId} data-testid={`strategy-sync-loose-row-${row.screenId}`}>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {row.screenName || row.screenId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                        {row.screenId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.screenType || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={options}
                        value={selected}
                        onChange={(_, value) => setLooseResolution(row.screenId, value)}
                        getOptionLabel={(option) => option.label}
                        groupBy={(option) => (
                          option.kind === 'block' ? (option.suggested ? 'Suggested' : 'Blocks') : ''
                        )}
                        renderGroup={(params) => (
                          <li key={params.key}>
                            {params.group && (
                              <ListSubheader component="div" className="MuiAutocomplete-groupLabel" sx={{ lineHeight: '32px' }}>
                                {params.group}
                              </ListSubheader>
                            )}
                            <ul style={{ padding: 0, listStyle: 'none' }}>{params.children}</ul>
                          </li>
                        )}
                        isOptionEqualToValue={(option, value) => (
                          option.kind === value.kind
                          && (option.kind !== 'block' || value.kind !== 'block'
                            || option.blockEntityId === value.blockEntityId)
                        )}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Keep loose (default)"
                            inputProps={{
                              ...params.inputProps,
                              'data-testid': `strategy-sync-loose-choice-${row.screenId}`,
                            }}
                          />
                        )}
                      />
                      {current?.action === 'new-block' && (
                        <TextField
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          placeholder="New block name"
                          value={newBlockNames[row.screenId] ?? ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            setNewBlockNames((prev) => ({ ...prev, [row.screenId]: value }));
                            setLooseResolution(row.screenId, { kind: 'new', label: '+ Create new block…' }, value);
                          }}
                          inputProps={{ 'data-testid': `strategy-sync-loose-new-name-${row.screenId}` }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      )}

      {(data?.foreignTagIgnored ?? []).length > 0 && (
        <Stack spacing={0.5}>
          <Typography sx={SECTION_HEADER_SX}>Ignored foreign tags</Typography>
          {(data?.foreignTagIgnored ?? []).map((entry) => (
            <Typography
              key={entry.screenId}
              variant="caption"
              color="warning.main"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              {entry.screenId} — belongs to strategy «{entry.strategyTag}» (block {entry.blockTag}); grouped as loose.
            </Typography>
          ))}
        </Stack>
      )}

      {visibleVariables.length > 0 && (
        <Stack spacing={1} data-testid="strategy-sync-variables-section">
          <Typography sx={SECTION_HEADER_SX}>
            Variables · {visibleVariables.length} difference{visibleVariables.length === 1 ? '' : 's'}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Variable</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Referenced by</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, width: 220 }}>Resolution</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleVariables.map((entry) => {
                const value = resolutions.variables?.[entry.variableId] ?? entry.defaultResolution;
                const isAdded = entry.status === 'added';
                const isRemoved = entry.status === 'removed';
                return (
                  <TableRow key={entry.variableId} data-testid={`strategy-sync-variable-row-${entry.variableId}`}>
                    <TableCell>
                      <Typography variant="body2" component="span">{entry.name || entry.variableId}</Typography>
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                        {entry.type || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={entry.status === 'changed' ? 'warning' : entry.status === 'added' ? 'success' : 'error'}
                        label={`${entry.status}${isAdded ? ` (${sides.source}-only)` : isRemoved ? ` (${sides.target}-only)` : ''}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entry.referenced ? `${entry.referencedByScreenIds.length} screen${entry.referencedByScreenIds.length === 1 ? '' : 's'}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={value}
                        onChange={(event) => setVariableResolution(entry.variableId, String(event.target.value))}
                        inputProps={{ 'data-testid': `strategy-sync-variable-resolution-${entry.variableId}` }}
                      >
                        {isAdded ? ([
                          <MenuItem key="overwrite" value="overwrite">Create on {sides.target}</MenuItem>,
                          <MenuItem key="drop" value="drop">Skip</MenuItem>,
                        ]) : isRemoved ? ([
                          <MenuItem key="preserve" value="preserve">Keep on {sides.target}</MenuItem>,
                          <MenuItem key="drop" value="drop">Remove from {sides.target}</MenuItem>,
                        ]) : ([
                          <MenuItem key="preserve" value="preserve">Keep {sides.target} definition</MenuItem>,
                          <MenuItem key="overwrite" value="overwrite">Take {sides.source} definition</MenuItem>,
                          <MenuItem key="fork" value="fork">Fork as copy_{entry.variableId}</MenuItem>,
                          <MenuItem key="drop" value="drop">Drop variable</MenuItem>,
                        ])}
                      </Select>
                      {value === 'fork' && (
                        <Typography variant="caption" color="text.secondary">
                          fork also writes copy_{entry.variableId} back to the strategy
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      )}

      {visibleAssets.length > 0 && (
        <Stack spacing={1} data-testid="strategy-sync-assets-section">
          <Typography sx={SECTION_HEADER_SX}>
            AI asset slots · {visibleAssets.length} difference{visibleAssets.length === 1 ? '' : 's'}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Screen · slot</TableCell>
                <TableCell sx={HEAD_CELL_SX}>{sides.target} side</TableCell>
                <TableCell sx={HEAD_CELL_SX}>{sides.source} side</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, width: 240 }}>Shown image</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAssets.map((entry) => {
                const candidates = Array.from(new Set(
                  [entry.targetSelectedExperimentId, entry.sourceSelectedExperimentId].filter(Boolean),
                )) as string[];
                const currentChoice = resolutions.assetSlots?.[entry.screenId]?.[entry.slotKey]?.selectedExperimentId
                  ?? entry.targetSelectedExperimentId
                  ?? '';
                return (
                  <TableRow
                    key={`${entry.screenId}-${entry.slotKey}`}
                    data-testid={`strategy-sync-asset-row-${entry.screenId}-${entry.slotKey}`}
                  >
                    <TableCell>
                      <Typography variant="body2">{entry.screenId} · {entry.slotKey}</Typography>
                      {!entry.hasMappedContentPath && (
                        <Typography variant="caption" color="text.secondary">
                          metadata-only slot (no content mapping)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entry.targetExperimentIds.length > 0
                          ? entry.targetExperimentIds.map((id) => (id === entry.targetSelectedExperimentId ? `${id} ✓` : id)).join(', ')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entry.sourceExperimentIds.length > 0
                          ? entry.sourceExperimentIds.map((id) => (id === entry.sourceSelectedExperimentId ? `${id} ✓` : id)).join(', ')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {entry.status === 'both-diverged' && candidates.length > 1 && entry.hasMappedContentPath ? (
                        <>
                          <Select
                            size="small"
                            fullWidth
                            value={currentChoice}
                            onChange={(event) => setAssetResolution(entry, String(event.target.value))}
                            inputProps={{ 'data-testid': `strategy-sync-asset-choice-${entry.screenId}-${entry.slotKey}` }}
                          >
                            {candidates.map((experimentId) => (
                              <MenuItem key={experimentId} value={experimentId}>
                                {experimentId === entry.targetSelectedExperimentId
                                  ? `Keep ${sides.target} pick (${experimentId})`
                                  : `Use ${sides.source} pick (${experimentId})`}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" color="text.secondary">
                            both experiments are kept as alternates
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {entry.status === 'source-only'
                            ? `new from ${sides.source} — merged as alternates`
                            : 'merged as alternates'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      )}

      {(data?.warnings ?? []).length > 0 && (
        <Alert severity="warning" data-testid="strategy-sync-warnings">
          <Stack spacing={0.5}>
            {(data?.warnings ?? []).map((warning, index) => (
              <Typography key={index} variant="body2">
                {warning}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {data && counts.would_replace > 0 && (
        <Typography variant="body2" color="text.secondary">
          Replaced screens take their content from the {sides.source}; canvas positions are kept.
          Screens without {sides.source}-side changes keep all polish.
        </Typography>
      )}
    </Stack>
  );
};

StrategySyncPlanView.displayName = 'StrategySyncPlanView';
